import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Eye, EyeOff } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { supabase } from "@/integrations/supabase/client";
import { migrateLegacyLogin } from "@/lib/legacy-auth.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { reset?: string } => ({
    reset: typeof search.reset === "string" ? search.reset : undefined,
  }),
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/login" },
      { title: "Σύνδεση — MrPanda.gr" },
      { name: "description", content: "Συνδέσου στον λογαριασμό σου στο MrPanda.gr για να δεις τις παραγγελίες, τη λίστα επιθυμιών και τα στοιχεία σου." },
      { name: "robots", content: "noindex, follow" },
    ] }),
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const { reset } = Route.useSearch();
  const migrate = useServerFn(migrateLegacyLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oops, setOops] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      setLoading(false);
      nav({ to: "/" });
      return;
    }

    // Only attempt legacy migration when the credentials were rejected.
    if (error.message === "Invalid login credentials") {
      try {
        const res = await migrate({ data: { email, password } });
        if (res.migrated) {
          const retry = await supabase.auth.signInWithPassword({ email, password });
          setLoading(false);
          if (!retry.error) {
            nav({ to: "/" });
            return;
          }
          setOops(true);
          return;
        }
      } catch {
        // fall through to generic error below
      }
      setLoading(false);
      setOops(true);
      return;
    }

    setLoading(false);
    setErr(error.message);
  };

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-md px-4 py-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="grid h-20 w-20 place-items-center rounded-full bg-accent text-5xl animate-bob">🐼</div>
          <h1 className="mt-3 text-2xl font-bold">Καλώς ήρθες πίσω!</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ο Mr Panda σε περίμενε 🎉 Συνδέσου για να συνεχίσεις.</p>
        </div>
        <form onSubmit={submit} className="animate-pop-in space-y-3 rounded-2xl border border-border p-6 shadow-sm mp-playful-panel">
          {reset === "success" && (
            <div className="rounded-md bg-primary/10 p-3 text-center text-sm font-medium text-primary">
              Ο κωδικός άλλαξε, συνδέσου 🎉
            </div>
          )}
          <label className="block text-sm">
            <span className="font-medium">📧 Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3" />
          </label>
          <label className="block text-sm">
            <span className="font-medium">🔒 Κωδικός</span>
            <div className="relative mt-1">
              <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? "text" : "password"} required className="h-10 w-full rounded-md border border-border bg-background px-3 pr-10" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Απόκρυψη κωδικού" : "Προβολή κωδικού"} className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>
          {err && <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">{err}</div>}
          <button disabled={loading} type="submit" className="mp-bouncy w-full rounded-full bg-primary py-2.5 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {loading ? "Σύνδεση..." : "Σύνδεση 🚀"}
          </button>
          <div className="text-center text-sm text-muted-foreground">
            Δεν έχεις λογαριασμό; <Link to="/signup" className="text-primary hover:underline">Εγγραφή</Link>
          </div>
          <div className="text-center text-xs text-muted-foreground">
            <Link to="/reset-password" className="hover:underline">Ξέχασες τον κωδικό;</Link>
          </div>
        </form>
      </div>

      <Dialog open={oops} onOpenChange={setOops}>
        <DialogContent className="mp-playful-panel max-w-sm rounded-3xl text-center">
          <DialogHeader className="items-center">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-accent text-5xl animate-bob">
              🙈
            </div>
            <DialogTitle className="mt-2 text-xl">Ουπς! Κάτι δεν πάει καλά 🐼</DialogTitle>
            <DialogDescription className="text-sm">
              Το email ή ο κωδικός δεν ταιριάζουν. Δοκίμασε ξανά ή άλλαξε τον κωδικό σου — ο Mr Panda σε καλύπτει! 💪
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Link
              to="/reset-password"
              className="mp-bouncy w-full rounded-full bg-primary py-2.5 text-center font-semibold text-primary-foreground hover:opacity-90"
            >
              Αλλαγή κωδικού 🔑
            </Link>
            <button
              type="button"
              onClick={() => setOops(false)}
              className="w-full rounded-full border border-border py-2.5 font-medium hover:bg-muted"
            >
              Δοκιμάζω ξανά 🔁
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StorefrontLayout>
  );
}
